import type { InjectionKey } from 'vue'
import type { ReadingPosition } from './useBookUrl'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'

export interface TocStateContext {
  tocItems: Ref<TocItemData[]>
  flatTocItems: Ref<TocItemData[]>
  selectedTocId: Readonly<Ref<string>>
  setSelectedTocId: (id: string) => void
  findByPosition: (position: ReadingPosition) => TocItemData | null
  findById: (id: string) => TocItemData | null
  findByPageAndAnchor: (pageNumber: string, anchor: string | undefined) => TocItemData | null
}

export const TocStateKey = Symbol('tocState') as InjectionKey<TocStateContext>

export function buildPositionKey(pageNumber: string, anchor?: string): string {
  if (anchor) {
    return `${pageNumber}#${anchor}`
  }
  return pageNumber
}

/** Provider: sets up TOC state for a book. Call in the page component. */
export function useTocStateProvider(bookId: MaybeRefOrGetter<string>) {
  const { data: tocItems, suspense } = useHierarchy(bookId)

  const tocIndex = computed(() => {
    const map = new Map<string, TocItemData>()
    const buildIndex = (items: TocItemData[]) => {
      items.forEach((item) => {
        map.set(item.id, item)
        if (item.children?.length) {
          buildIndex(item.children)
        }
      })
    }
    if (tocItems.value) {
      buildIndex(tocItems.value)
    }
    return map
  })

  const flatTocItems = computed(() => Array.from(tocIndex.value.values()))
  const selectedTocId = ref<string>('')

  function findByPosition(position: ReadingPosition): TocItemData | null {
    if (!position.pageNumber) {
      return null
    }

    if (position.anchor) {
      const exactMatch = flatTocItems.value.find(
        item => item.pageNumberString === position.pageNumber && item.fragmentId === position.anchor,
      )
      if (exactMatch) {
        return exactMatch
      }
    }

    return flatTocItems.value.find(item => item.pageNumberString === position.pageNumber) ?? null
  }

  function findById(id: string): TocItemData | null {
    return tocIndex.value.get(id) ?? null
  }

  function findByPageAndAnchor(pageNumber: string, anchor: string | undefined): TocItemData | null {
    return flatTocItems.value.find(it =>
      (anchor && it.fragmentId === anchor)
      || pageNumber === it.pageNumberString,
    ) ?? null
  }

  function setSelectedTocId(id: string) {
    selectedTocId.value = id
  }

  const context: TocStateContext = {
    tocItems: tocItems as unknown as Ref<TocItemData[]>,
    flatTocItems: flatTocItems as unknown as Ref<TocItemData[]>,
    selectedTocId: readonly(selectedTocId),
    setSelectedTocId,
    findByPosition,
    findById,
    findByPageAndAnchor,
  }

  provide(TocStateKey, context)

  return {
    ...context,
    suspense,
  }
}

/** Consumer: access TOC state from child components. */
export function useTocState(): TocStateContext {
  const context = inject(TocStateKey)
  if (!context) {
    throw new Error('useTocState must be used within a component that called useTocStateProvider')
  }
  return context
}
